import { ImagePlus } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const ACCEPTED_IMAGE_TYPES = '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp';

export function PerspectiveImageUpload({
  currentImageUrl,
  disabled,
  file,
  label,
  required,
  onChange,
}: {
  currentImageUrl?: string;
  disabled: boolean;
  file: File | null;
  label: string;
  required: boolean;
  onChange: (file: File | null) => void;
}) {
  const { t } = useTranslation();
  const selectedImageUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const previewUrl = selectedImageUrl ?? currentImageUrl;

  useEffect(
    () => () => {
      if (selectedImageUrl) URL.revokeObjectURL(selectedImageUrl);
    },
    [selectedImageUrl]
  );

  function selectFirstImage(files: FileList | null) {
    onChange(files?.[0] ?? null);
  }

  return (
    <label
      className="flex w-full cursor-pointer flex-col gap-2 sm:col-span-2"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        if (!disabled) selectFirstImage(event.dataTransfer.files);
      }}
    >
      <span className="select-none text-xs text-text-primary">{label}</span>
      <span
        className={cn(
          'flex min-h-32 items-center gap-4 rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 transition-colors',
          'focus-within:border-primary-light focus-within:ring-1 focus-within:ring-primary-light hover:border-primary-light hover:bg-primary-lighter/20',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <input
          type="file"
          className="sr-only"
          required={required}
          disabled={disabled}
          accept={ACCEPTED_IMAGE_TYPES}
          onChange={(event) => selectFirstImage(event.target.files)}
        />
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={t('preview')}
            className="h-24 w-36 shrink-0 rounded-sm border border-slate-200 bg-white object-cover"
          />
        ) : (
          <span className="flex size-16 shrink-0 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm">
            <ImagePlus aria-hidden className="size-7" />
          </span>
        )}
        <span className="min-w-0 space-y-1">
          <span className="block truncate text-sm font-medium text-text-primary">
            {file?.name ?? t('uploadImage')}
          </span>
          <span className="block text-xs text-text-secondary">{t('uploadDocumentText')}</span>
          <span className="block text-xs text-text-secondary">PNG, JPG, WEBP</span>
        </span>
      </span>
    </label>
  );
}
